class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.bindCartLinkInterceptor();
    this.openDrawerFromCartPageRedirect();
  }

  cartPathname() {
    const cartUrl = window.routes?.cart_url || '/cart';
    try {
      return this.normalizePath(new URL(cartUrl, window.location.origin).pathname);
    } catch (error) {
      return '/cart';
    }
  }

  normalizePath(pathname) {
    if (!pathname) return '/';
    if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
    return pathname;
  }

  isCartPath(pathname) {
    return this.normalizePath(pathname) === this.cartPathname();
  }

  openDrawerFromCartPageRedirect() {
    if (this.isCartPath(window.location.pathname)) return;

    const openKey = 'duos-open-cart-drawer';
    let shouldOpen = false;

    try {
      if (sessionStorage.getItem(openKey) === '1') {
        sessionStorage.removeItem(openKey);
        shouldOpen = true;
      }
    } catch (error) {}

    if (!shouldOpen && window.location.hash === '#open-cart') {
      shouldOpen = true;
      history.replaceState(null, '', window.location.pathname + window.location.search);
    }

    if (shouldOpen) this.open(document.querySelector('#cart-icon-bubble'));
  }

  bindCartLinkInterceptor() {
    const openFromCartLink = (event) => {
      if (event.defaultPrevented) return;
      if (event.type === 'auxclick' && event.button !== 1) return;

      const link = event.target.closest('a[href]');
      if (!link || link.target === '_blank') return;

      let url;
      try {
        url = new URL(link.href, window.location.origin);
      } catch (error) {
        return;
      }

      if (url.origin !== window.location.origin || !this.isCartPath(url.pathname)) return;

      event.preventDefault();
      this.open(link);
    };

    document.addEventListener('click', openFromCartLink);
    document.addEventListener('auxclick', openFromCartLink);
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-open-cart-drawer]');
      if (!trigger || event.defaultPrevented) return;
      event.preventDefault();
      this.open(trigger);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartFreeGift extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onSelect.bind(this));
  }

  withoutManagedGiftNote(note = '') {
    const prefixes = [
      'Prosím si ako darček ',
      'Prosim si ako darcek ',
      'Prosím si jako dárek ',
      'Please include as my gift: ',
    ];
    return note
      .split(/\r?\n/)
      .filter((line) => !prefixes.some((prefix) => line.trim().startsWith(prefix)))
      .join('\n')
      .trim();
  }

  async onSelect(event) {
    const button = event.target.closest('[data-gift-note]');
    if (!button || button.disabled || this.classList.contains('is-loading')) return;

    const cartDrawer = this.closest('cart-drawer');
    const sections = cartDrawer.getSectionsToRender().map((section) => section.id);

    this.classList.add('is-loading');
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    this.querySelector('.cart-drawer__gift-error')?.classList.add('hidden');
    this.querySelectorAll('button').forEach((giftButton) => (giftButton.disabled = true));

    try {
      const cartResponse = await fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      if (!cartResponse.ok) throw new Error('Unable to read the cart note.');

      const visibleNote = cartDrawer.querySelector('textarea[name="note"]')?.value;
      const noteWithoutGift = this.withoutManagedGiftNote(visibleNote ?? cart.note);
      const note = [noteWithoutGift, button.dataset.giftNote].filter(Boolean).join('\n');
      const updateResponse = await fetch(`${routes.cart_update_url}`, {
        ...fetchConfig(),
        body: JSON.stringify({
          note,
          attributes: { _black_november_gift_variant: button.dataset.giftVariantId },
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const updatedState = await updateResponse.json();
      if (!updateResponse.ok || updatedState.errors) {
        throw new Error(updatedState.description || updatedState.message || 'Unable to save the selected gift.');
      }

      cartDrawer.renderContents(updatedState);
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'free-gift-picker',
        productVariantId: button.dataset.giftVariantId,
        cartData: updatedState,
      });
    } catch (error) {
      console.error(error);
      this.querySelector('.cart-drawer__gift-error')?.classList.remove('hidden');
      this.classList.remove('is-loading');
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
      this.querySelectorAll('button').forEach((giftButton) => {
        giftButton.disabled = giftButton.classList.contains('is-selected');
      });
    }
  }
}

customElements.define('cart-free-gift', CartFreeGift);

class CartDrawerDiscount extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[name="discount"]');
    this.errorElement = this.querySelector('.cart-drawer__discount-error');
    this.input.setCustomValidity('');
    this.form.addEventListener('submit', this.onApply.bind(this));
    this.querySelectorAll('[data-remove-discount]').forEach((button) =>
      button.addEventListener('click', this.onRemove.bind(this))
    );
  }

  get appliedCodes() {
    return (this.dataset.codes || '').split(',').filter(Boolean);
  }

  onApply(event) {
    event.preventDefault();
    const code = this.input.value.trim();
    if (!code) return;
    if (this.appliedCodes.some((applied) => applied.toLowerCase() === code.toLowerCase())) {
      this.showError(window.duosDiscountStrings?.alreadyUsed);
      return;
    }
    this.updateDiscounts(this.appliedCodes.concat(code), code);
  }

  onRemove(event) {
    const code = event.currentTarget.dataset.removeDiscount;
    this.updateDiscounts(
      this.appliedCodes.filter((applied) => applied !== code),
      null
    );
  }

  updateDiscounts(codes, newCode) {
    this.hideError();
    this.classList.add('cart-drawer__discount--loading');
    const cartDrawer = this.closest('cart-drawer');
    const body = JSON.stringify({
      discount: codes.join(','),
      sections: cartDrawer.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.json())
      .then((parsedState) => {
        const returnedCodes = parsedState.discount_codes;
        if (newCode && Array.isArray(returnedCodes)) {
          const applied = returnedCodes.find(
            (discount) => discount.code.toLowerCase() === newCode.toLowerCase() && discount.applicable
          );
          if (!applied) {
            const revertBody = JSON.stringify({
              discount: codes.filter((code) => code !== newCode).join(','),
            });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body: revertBody } });
            this.showError(window.duosDiscountStrings?.notApplicable);
            return;
          }
        }
        cartDrawer.renderContents(parsedState);
      })
      .catch(() => {
        this.showError(window.duosDiscountStrings?.failed);
      })
      .finally(() => {
        this.classList.remove('cart-drawer__discount--loading');
      });
  }

  showError(message) {
    this.errorElement.textContent = message;
    this.errorElement.classList.remove('hidden');
  }

  hideError() {
    this.errorElement.textContent = '';
    this.errorElement.classList.add('hidden');
  }
}

customElements.define('cart-drawer-discount', CartDrawerDiscount);
